import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Badge, Dialog, Select, Textarea } from '@dreamtree-org/twreact-ui';
import { ShieldCheck, RotateCcw, Siren, ClipboardCheck, PackageX, Repeat } from 'lucide-react';
import { PageHeader, StatCard, SectionCard, QueryState, EmptyState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import { DOA_DAYS, REPEAT_REPAIR_DAYS } from '../domain';
import { DataTable, ClaimBadge, useAction, ActionResult, formatDateTime } from '../components';

const EXCLUSION_OPTIONS = [
  { value: '', label: 'No exclusion found' },
  { value: 'liquid_damage', label: 'New liquid damage' },
  { value: 'physical_damage', label: 'New physical damage' },
  { value: 'seal_broken', label: 'Service seal broken' },
];

function Flags({ flags }) {
  return (
    <span className="flex flex-wrap gap-1">
      {flags.doa ? (
        <Badge variant="error" size="sm">
          DOA
        </Badge>
      ) : null}
      {flags.repeat ? (
        <Badge variant="warning" size="sm">
          Repeat repair
        </Badge>
      ) : null}
      {!flags.doa && !flags.repeat ? <span className="opacity-60">—</span> : null}
    </span>
  );
}

function EligibilityCard({ warranties, doaDays, act, onClaimed }) {
  const [warrantyId, setWarrantyId] = useState('');
  const [exclusion, setExclusion] = useState('');
  const [result, setResult] = useState(null);
  const [description, setDescription] = useState('');

  const check = async () => {
    const res = await act.run('serviceWarrantyCheck', { warrantyId, exclusion }, '', 'check');
    setResult(res ? res.data : null);
  };
  const claim = async () => {
    const res = await act.run('serviceWarrantyClaim', { warrantyId, description, exclusion }, 'Claim registered.');
    if (res) {
      setResult(null);
      setDescription('');
      onClaimed();
    }
  };

  return (
    <SectionCard title="Eligibility check">
      <p className="text-sm opacity-70">
        Repairs carry a 90-day warranty; items sold online carry the seller warranty from delivery. Claims within {doaDays} days are flagged DOA; a return within {REPEAT_REPAIR_DAYS} days of a repair is a repeat repair.
      </p>
      <Select
        label="Warranty"
        searchable
        closeOnSelect
        placeholder="Find by customer, device or job"
        options={warranties.map((w) => ({ value: w.id, label: `${w.id} · ${w.customer.name || 'Customer'} · ${w.device.model} (${w.jobId || w.orderId || w.source})` }))}
        value={warrantyId}
        onChange={(v) => {
          setWarrantyId(v || '');
          setResult(null);
        }}
      />
      <Select
        label="Inspection finding"
        closeOnSelect
        options={EXCLUSION_OPTIONS}
        value={exclusion}
        onChange={(v) => {
          setExclusion(v || '');
          setResult(null);
        }}
      />
      <Button variant="outline" onClick={check} disabled={!warrantyId} loading={act.busy === 'check'} leftIcon={<ClipboardCheck size={16} />}>
        Check eligibility
      </Button>
      {result ? (
        <Alert variant={result.eligible ? 'success' : 'error'} title={result.eligible ? 'Eligible for warranty repair' : 'Not eligible'}>
          <div className="space-y-1 text-sm">
            <div>
              Valid {formatDate(result.warranty.startsAt)} – {formatDate(result.warranty.endsAt)} · {result.warranty.coverage}
            </div>
            {result.reasons.map((r) => (
              <div key={r}>• {r}</div>
            ))}
            <div className="flex flex-wrap gap-1 pt-1">
              <Flags flags={{ doa: result.doa, repeat: result.repeat }} />
            </div>
          </div>
        </Alert>
      ) : null}
      {result ? (
        <div className="space-y-2">
          <Textarea label="Customer's complaint" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What failed again, and when" />
          <Button onClick={claim} disabled={!description.trim()} loading={act.busy === 'serviceWarrantyClaim'}>
            {result.eligible ? 'Register claim' : 'Register as not eligible'}
          </Button>
        </div>
      ) : null}
    </SectionCard>
  );
}

/** The DOA window from the platform returns_warranty policy (desk payload); the domain constant is only a fallback. */
const doaDaysOf = (d) => (d && d.terms && Number.isInteger(d.terms.doa_days) ? d.terms.doa_days : DOA_DAYS);

/** SERV-05 Repair warranty & returns: claims, eligibility, reopen as a new job, DOA/repeat flags, escalations. */
export default function Warranty() {
  const nav = useNavigate();
  const q = useQuery('serviceWarranty', null, { select: (r) => r.data });
  const act = useAction();
  const [escalating, setEscalating] = useState(null);
  const [reason, setReason] = useState('');

  const reopen = async (c) => {
    const res = await act.run('serviceWarrantyReopen', { claimId: c.id }, '', c.id);
    if (res) nav(`/tickets/${res.data.newTicketId}`);
  };
  const escalate = async () => {
    const res = await act.run('serviceWarrantyEscalate', { claimId: escalating.id, reason }, `${escalating.id} escalated to the service manager.`);
    if (res) {
      setEscalating(null);
      setReason('');
      q.reload();
    }
  };

  const columns = [
    {
      key: 'id',
      label: 'Claim',
      render: (c) => (
        <div>
          <div className="font-semibold">{c.id}</div>
          <div className="text-xs opacity-70">{formatDateTime(c.createdAt)}</div>
        </div>
      ),
    },
    {
      key: 'device',
      label: 'Customer and device',
      render: (c) => (
        <div>
          <div>{c.customer.name}</div>
          <div className="text-xs opacity-70">
            {c.device ? c.device.model : ''} · {c.jobId ? `repair ${c.jobId}` : c.orderId ? `order ${c.orderId}` : ''}{c.source === 'storefront' ? ' · raised online' : ''}
          </div>
        </div>
      ),
    },
    { key: 'description', label: 'Complaint', wrap: true, render: (c) => <span className="text-sm">{c.description}</span> },
    { key: 'flags', label: 'Flags', render: (c) => <Flags flags={c.flags} /> },
    {
      key: 'status',
      label: 'Status',
      render: (c) => (
        <div className="space-y-1">
          <ClaimBadge status={c.status} />
          {c.escalation ? <div className="text-xs opacity-70">{c.escalation.reason}</div> : null}
          {c.decisionReason ? <div className="text-xs opacity-70">{c.decisionReason}</div> : null}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (c) =>
        c.newTicketId ? (
          <Button size="sm" variant="ghost" onClick={() => nav(`/tickets/${c.newTicketId}`)}>
            Open {c.newTicketId}
          </Button>
        ) : c.status === 'rejected' ? (
          <span className="opacity-60 text-sm">—</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            <Button size="sm" onClick={() => reopen(c)} loading={act.busy === c.id} leftIcon={<RotateCcw size={14} />}>
              Reopen as ticket
            </Button>
            {c.status !== 'escalated' ? (
              <Button size="sm" variant="outline" onClick={() => setEscalating(c)} leftIcon={<Siren size={14} />}>
                Escalate
              </Button>
            ) : null}
          </span>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Warranty & returns" subtitle="Warranty claims from the desk and the storefront, eligibility and rework" />
      <ActionResult action={act} />
      <QueryState query={q}>
        {(d) => (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatCard label="Open claims" value={d.claims.filter((c) => ['open', 'escalated'].includes(c.status)).length} icon={<ShieldCheck size={18} />} />
              <StatCard label="DOA" value={d.claims.filter((c) => c.flags.doa).length} hint={`within ${doaDaysOf(d)} days`} icon={<PackageX size={18} />} />
              <StatCard label="Repeat repairs" value={d.claims.filter((c) => c.flags.repeat).length} hint={`within ${REPEAT_REPAIR_DAYS} days`} icon={<Repeat size={18} />} />
              <StatCard label="Escalations" value={d.claims.filter((c) => c.status === 'escalated').length} icon={<Siren size={18} />} />
            </div>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <SectionCard title="Claims" className="xl:col-span-2">
                {d.claims.length === 0 ? <EmptyState title="No warranty claims" /> : <DataTable data={d.claims} columns={columns} mobileMaxFields={6} />}
              </SectionCard>
              <div className="space-y-4">
                <EligibilityCard warranties={d.warranties} doaDays={doaDaysOf(d)} act={act} onClaimed={q.reload} />
                <SectionCard title="Warranties">
                  <ul className="divide-y divide-[var(--app-border)] text-sm">
                    {d.warranties.map((w) => (
                      <li key={w.id} className="py-2 flex justify-between gap-2">
                        <span className="min-w-0">
                          <span className="font-medium">{w.id}</span> · {w.customer.name}
                          <span className="block text-xs opacity-70">
                            {w.device.model} · {w.source === 'order_item' ? `order ${w.orderId}` : `repair ${w.jobId}`} · until {formatDate(w.endsAt)}
                          </span>
                        </span>
                        <Badge variant={w.status === 'active' ? 'success' : 'default'} size="sm">
                          {w.status}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </SectionCard>
              </div>
            </div>
          </>
        )}
      </QueryState>

      <Dialog
        isOpen={Boolean(escalating)}
        onClose={() => setEscalating(null)}
        title={escalating ? `Escalate ${escalating.id}` : ''}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEscalating(null)}>
              Cancel
            </Button>
            <Button onClick={escalate} disabled={!reason.trim()} loading={act.busy === 'serviceWarrantyEscalate'}>
              Escalate
            </Button>
          </div>
        }
      >
        <Textarea label="Reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Same fault twice; check the part supplier batch." />
        <p className="text-xs opacity-70 mt-2">Escalations go to the service manager for a decision.</p>
      </Dialog>
    </div>
  );
}
